<script setup>
import { computed, ref, watch } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  fields: { type: Array, default: () => [] },
  publicUrl: { type: String, default: '' },
  webhookUrl: { type: String, default: '' },
  isSaved: { type: Boolean, default: false },
  formName: { type: String, default: '' },
  customHostnameState: { type: Object, default: () => ({}) },
  customHostnameTarget: { type: String, default: '' },
});

const { t } = useI18n();

const formSlug = computed(() => {
  try {
    return new URL(props.publicUrl).pathname.split('/').pop() || '';
  } catch (error) {
    return '';
  }
});

const iframeSnippet = computed(
  () =>
    `<iframe src="${props.publicUrl}" style="width:100%;border:0;min-height:600px" title="${props.formName}"></iframe>`
);

const scriptSnippet = computed(() => {
  const origin = props.publicUrl
    ? new URL(props.publicUrl).origin
    : window.location.origin;
  return [
    // eslint-disable-next-line no-useless-escape -- <\/script> escapado p/ o parser do SFC Vue não fechar o bloco <script setup> cedo
    `<script src="${origin}/forms/embed.js" data-form="${formSlug.value}"><\/script>`,
    `<div data-chatsuite-form="${formSlug.value}"></div>`,
  ].join('\n');
});

const enabled = defineModel('enabled', { type: Boolean, default: true });
const customDomain = defineModel('customDomain', {
  type: String,
  default: '',
});
const mappingEntries = defineModel('mappingEntries', {
  type: Array,
  default: () => [],
});

const copy = async value => {
  try {
    await copyTextToClipboard(value);
    useAlert(t('FORMS_MGMT.EDITOR.PUBLISH.COPIED'));
  } catch (error) {
    useAlert(t('FORMS_MGMT.EDITOR.PUBLISH.COPY_ERROR'));
  }
};

const openUrl = url => {
  window.open(url, '_blank', 'noopener noreferrer');
};

// QR do link público: quem escaneia abre o formulário e responde na hora.
// Gerado no navegador (lib `qrcode`), sem chamada ao servidor.
const qrDataUrl = ref('');
watch(
  () => props.publicUrl,
  async url => {
    qrDataUrl.value = '';
    if (!url) return;
    try {
      qrDataUrl.value = await QRCode.toDataURL(url, { width: 480, margin: 1 });
    } catch (error) {
      qrDataUrl.value = '';
    }
  },
  { immediate: true }
);

const downloadQr = () => {
  const a = document.createElement('a');
  a.href = qrDataUrl.value;
  a.download = `qr-formulario-${formSlug.value || 'form'}.png`;
  a.click();
};

const addMapping = () => {
  mappingEntries.value = [
    ...mappingEntries.value,
    { payloadKey: '', fieldKey: '' },
  ];
};

const updateMapping = (index, key, value) => {
  const updated = [...mappingEntries.value];
  updated[index] = { ...updated[index], [key]: value };
  mappingEntries.value = updated;
};

const removeMapping = index => {
  mappingEntries.value = mappingEntries.value.filter((_, i) => i !== index);
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.PUBLISH.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.PUBLISH.DESCRIPTION') }}
      </p>
    </div>

    <label class="flex items-center gap-2">
      <Switch v-model="enabled" />
      <span class="text-sm font-medium text-n-slate-12">
        {{
          enabled
            ? t('FORMS_MGMT.EDITOR.PUBLISH.ENABLED')
            : t('FORMS_MGMT.EDITOR.PUBLISH.DISABLED')
        }}
      </span>
    </label>

    <div
      v-if="!isSaved"
      class="text-sm text-n-slate-11 border border-dashed border-n-weak rounded-md p-4"
    >
      {{ t('FORMS_MGMT.EDITOR.PUBLISH.SAVE_FIRST') }}
    </div>

    <template v-else>
      <div class="flex flex-col gap-2 rounded-md border border-n-weak p-4">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.PUBLISH.DOMAIN_TITLE') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.PUBLISH.DOMAIN_HINT') }}
        </p>
        <input
          v-model="customDomain"
          type="text"
          autocomplete="off"
          :placeholder="t('FORMS_MGMT.EDITOR.PUBLISH.DOMAIN_PLACEHOLDER')"
          class="form-input text-sm"
        />
        <p
          v-if="customDomain && customHostnameTarget"
          class="text-xs text-n-slate-11"
        >
          {{
            t('FORMS_MGMT.EDITOR.PUBLISH.DOMAIN_CNAME', {
              target: customHostnameTarget,
            })
          }}
        </p>
        <p
          v-if="customHostnameState.status || customHostnameState.error"
          class="text-xs"
          :class="
            customHostnameState.ssl_status === 'active'
              ? 'text-n-teal-11'
              : 'text-n-amber-11'
          "
        >
          {{
            customHostnameState.error ||
            t('FORMS_MGMT.EDITOR.PUBLISH.DOMAIN_STATUS', {
              status:
                customHostnameState.ssl_status || customHostnameState.status,
            })
          }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.PUBLISH.PUBLIC_URL') }}
        </span>
        <div class="flex items-center gap-2">
          <input
            :value="publicUrl"
            type="text"
            readonly
            class="form-input flex-1 text-sm"
          />
          <Button
            icon="i-lucide-copy"
            slate
            faded
            :label="t('FORMS_MGMT.EDITOR.PUBLISH.COPY')"
            @click="copy(publicUrl)"
          />
          <Button
            icon="i-lucide-external-link"
            slate
            faded
            :label="t('FORMS_MGMT.EDITOR.PUBLISH.OPEN')"
            @click="openUrl(publicUrl)"
          />
        </div>
      </div>

      <div
        v-if="qrDataUrl"
        class="flex items-center gap-4 border border-n-weak rounded-md p-4"
        data-testid="form-qr"
      >
        <img
          :src="qrDataUrl"
          data-testid="form-qr-image"
          class="size-28 shrink-0 rounded-md bg-white p-1"
          :alt="t('FORMS_MGMT.EDITOR.PUBLISH.QR_ALT')"
        />
        <div class="flex flex-col gap-2 min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.QR_TITLE') }}
          </span>
          <p class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.QR_HINT') }}
          </p>
          <Button
            icon="i-lucide-download"
            slate
            faded
            xs
            class="w-fit"
            data-testid="form-qr-download"
            :label="t('FORMS_MGMT.EDITOR.PUBLISH.QR_DOWNLOAD')"
            @click="downloadQr"
          />
        </div>
      </div>

      <div class="flex flex-col gap-3 border border-n-weak rounded-md p-4">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.EMBED_TITLE') }}
          </span>
          <p class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.EMBED_DESCRIPTION') }}
          </p>
        </div>

        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.EMBED_IFRAME_LABEL') }}
          </span>
          <div class="flex items-start gap-2">
            <pre
              class="form-input flex-1 text-xs whitespace-pre-wrap break-all font-mono py-2"
              >{{ iframeSnippet }}</pre
            >
            <Button
              icon="i-lucide-copy"
              slate
              faded
              xs
              :label="t('FORMS_MGMT.EDITOR.PUBLISH.COPY')"
              @click="copy(iframeSnippet)"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.EMBED_SCRIPT_LABEL') }}
          </span>
          <div class="flex items-start gap-2">
            <pre
              class="form-input flex-1 text-xs whitespace-pre-wrap break-all font-mono py-2"
              >{{ scriptSnippet }}</pre
            >
            <Button
              icon="i-lucide-copy"
              slate
              faded
              xs
              :label="t('FORMS_MGMT.EDITOR.PUBLISH.COPY')"
              @click="copy(scriptSnippet)"
            />
          </div>
          <p class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.EMBED_SCRIPT_HINT') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.PUBLISH.WEBHOOK_URL') }}
        </span>
        <div class="flex items-center gap-2">
          <input
            :value="webhookUrl"
            type="text"
            readonly
            class="form-input flex-1 text-sm"
          />
          <Button
            icon="i-lucide-copy"
            slate
            faded
            :label="t('FORMS_MGMT.EDITOR.PUBLISH.COPY')"
            @click="copy(webhookUrl)"
          />
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.PUBLISH.WEBHOOK_HINT') }}
        </p>
      </div>

      <div class="flex flex-col gap-2 border border-n-weak rounded-md p-4">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.MAPPING_TITLE') }}
          </span>
          <p class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.PUBLISH.MAPPING_DESCRIPTION') }}
          </p>
        </div>

        <p v-if="!mappingEntries.length" class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.PUBLISH.MAPPING_EMPTY') }}
        </p>

        <div
          v-for="(entry, index) in mappingEntries"
          :key="index"
          class="flex items-center gap-2"
        >
          <input
            :value="entry.payloadKey"
            type="text"
            class="form-input flex-1"
            :placeholder="
              t('FORMS_MGMT.EDITOR.PUBLISH.MAPPING_PAYLOAD_KEY_PLACEHOLDER')
            "
            @input="
              event => updateMapping(index, 'payloadKey', event.target.value)
            "
          />
          <i class="i-lucide-arrow-right text-n-slate-9 shrink-0" />
          <select
            :value="entry.fieldKey"
            class="form-select flex-1"
            @change="
              event => updateMapping(index, 'fieldKey', event.target.value)
            "
          >
            <option value="">
              {{ t('FORMS_MGMT.EDITOR.PUBLISH.MAPPING_FIELD') }}
            </option>
            <option v-for="field in fields" :key="field.key" :value="field.key">
              {{ field.label || field.key }}
            </option>
          </select>
          <Button
            icon="i-lucide-x"
            ruby
            xs
            faded
            @click="removeMapping(index)"
          />
        </div>

        <Button
          icon="i-lucide-plus"
          slate
          faded
          class="w-fit"
          :label="t('FORMS_MGMT.EDITOR.PUBLISH.MAPPING_ADD')"
          @click="addMapping"
        />
      </div>
    </template>
  </div>
</template>
